// import { UnprocessedOrderType } from '@business/pages/OrderManage/shared/types';
// import { RootDispatch } from '..';

import poiInfoCache from '../../utils/poiInfoCache';

/** 订单提醒 */
export type OrderNotification = {
    content: string;
    href?: string;
    wmPoiId?: string;
    wmPoiName?: string;
    /** 消息已读回执埋点（灵犀MC) */
    readReceiptConfig?: {
        cid: string;
        mc: string;
        custom?: object;
    };
};

/** 消息通知 */
export type MessageNotification = {
    /** 标题 */
    title: string;
    /** 内容 */
    content: string;
    /** 消息所属门店ID */
    wmPoiId?: string;
    wmPoiName?: string;
    /** 跳转链接 */
    href?: string;
    /** 跳转链接文案 */
    linkText?: string;
    /** 消息已读回执埋点（灵犀MC) */
    readReceiptConfig?: {
        cid: string;
        mc: string;
        custom?: object;
    };
    /** 消息ID， me后台的同一个消息发给不同门店，noticeId是一致的 */
    noticeId?: string;
    /**  同一个消息，发给不同门店不一致 */
    wmEPoiMsgId: string;
    /**  同一个消息，发给不同门店不一致 */
    wmEMsgTraceId: string;
    /** 模板名 (可选，统一触达消息已读上报使用) */
    templateName?: string;
    /**
     * 消息类型 (可选，统一触达消息已读上报使用)
     * 1 站内Push / 2 文字弹窗 / 4图片通知 / 7 铃声提醒
     * */
    noticeType?: '1' | '2' | '4' | '7';
    /** 当 noticeType = 2 时，此字段用于标识文字弹框类型（0-跳转链接弹窗 1-跳转站内信弹窗 2-功能定制弹窗） */
    dialogType?: 0 | 1 | 2;
    /** 当 noticeType = 2 时，此字段为弹框定制功能配置 */
    customizeDialogConfig?: {
        /** 左按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
        leftBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
        /** 右按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
        rightBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
        leftBtnConfig: {
            /** 左按钮文本 */
            buttonText: string;
            /** 1-关闭 2-操作 3-跳转*/
            type: 1 | 2 | 3;
            /** 操作成功时的toast，如果未配置，则使用接口返回的结果 */
            succToast?: string;
            /** 操作失败时的toast，如果未配置，则使用接口返回的结果 */
            failToast?: string;
            /** type=2时存在，app和pc点击按钮之后调用的请求url */
            url?: string;
            /** type=2时存在，app点击按钮之后调用的请求url，app端优先选择appUrl，若appUrl不存在则选择url */
            appUrl?: string;
            /** type=2时存在，pc点击按钮之后调用的请求url，pc端优先选择pcUrl，若pcUrl不存在则选择url */
            pcUrl?: string;
        };
        rightBtnConfig: {
            /** 右按钮文本 */
            buttonText: string;
            /** 1-关闭 2-操作 3-跳转*/
            type: 1 | 2 | 3;
            /** 操作成功时的toast，如果未配置，则使用接口返回的结果 */
            succToast?: string;
            /** 操作失败时的toast，如果未配置，则使用接口返回的结果 */
            failToast?: string;
            /** type=2时存在，app和pc点击按钮之后调用的请求url */
            url?: string;
            /** type=2时存在，app点击按钮之后调用的请求url，app端优先选择appUrl，若appUrl不存在则选择url */
            appUrl?: string;
            /** type=2时存在，pc点击按钮之后调用的请求url，pc端优先选择pcUrl，若pcUrl不存在则选择url */
            pcUrl?: string;
        };
        /** 透传埋点 */
        utmContent: string;
        /** 1 单按钮弹窗，只使用右按钮配置。其他值保持原有逻辑 */
        buttonCnt?: number;
    };
};

type State = {
    /** 当前展示中的订单提醒 */
    orderNotification: OrderNotification | null;
    /** 弱提醒列表 */
    weakNotificationList: MessageNotification[];
    /** 强提醒列表 */
    strongNotificationList: MessageNotification[];
};

// 强提醒去重缓存
let strongNotificationFilterKeywordList: string[] = [];

// 弱提醒去重缓存
let weakNotificationFilterKeywordList: string[] = [];

const addWmPoiNameToNotification = (notification: MessageNotification) => {
    if (notification.wmPoiId) {
        const poiInfo = poiInfoCache.getPoiInfoById(Number(notification.wmPoiId));
        if (poiInfo) {
            notification.wmPoiName = poiInfo.poiName;
        }
    }
};

/**
 * push通知消息
 */
const pushNotification = {
    name: 'pushNotification',
    state: {
        orderNotification: null,
        weakNotificationList: [],
        strongNotificationList: [],
    } as State,
    reducers: {
        addOrderNotification(state: State, payload: OrderNotification | null) {
            const notification = payload;

            if (notification !== null && notification.wmPoiId) {
                const poiInfo = poiInfoCache.getPoiInfoById(Number(notification.wmPoiId));
                if (poiInfo) {
                    notification.wmPoiName = poiInfo.poiName;
                }
            }

            return {
                ...state,
                orderNotification: notification,
            };
        },
        addWeakNotification(state: State, payload: MessageNotification) {
            const notification = payload;

            if (
                weakNotificationFilterKeywordList.includes(notification.content) ||
                (notification.noticeId &&
                    weakNotificationFilterKeywordList.includes(notification.noticeId))
            ) {
                // 如果【相同内容】或【相同 noticeId】 的通知已经展示过，则不再展示
                return state;
            }

            addWmPoiNameToNotification(notification);

            // 将 内容 和 noticeId 加入过滤关键字列表
            weakNotificationFilterKeywordList.unshift(notification.content);
            if (notification.noticeId) {
                weakNotificationFilterKeywordList.unshift(notification.noticeId);
            }
            weakNotificationFilterKeywordList = weakNotificationFilterKeywordList.slice(0, 1000);

            return {
                ...state,
                weakNotificationList: [payload, ...state.weakNotificationList],
            };
        },
        clearWeakNotification(state: State) {
            return {
                ...state,
                weakNotificationList: [],
            };
        },
        closeWeakNotification(state: State, payload: { wmEPoiMsgId: string }) {
            return {
                ...state,
                weakNotificationList: state.weakNotificationList.filter(
                    item => item.wmEPoiMsgId !== payload.wmEPoiMsgId,
                ),
            };
        },
        addStrongNotification(state: State, payload: MessageNotification) {
            const notification = payload;

            if (
                strongNotificationFilterKeywordList.includes(notification.content) ||
                (notification.noticeId &&
                    strongNotificationFilterKeywordList.includes(notification.noticeId))
            ) {
                // 如果【相同内容】或【相同 noticeId】 的通知已经展示过，则不再展示
                return state;
            }

            addWmPoiNameToNotification(notification);

            // 将 内容 和 noticeId 加入过滤关键字列表
            strongNotificationFilterKeywordList.unshift(notification.content);
            if (notification.noticeId) {
                strongNotificationFilterKeywordList.unshift(notification.noticeId);
            }
            strongNotificationFilterKeywordList = strongNotificationFilterKeywordList.slice(
                0,
                1000,
            );

            return {
                ...state,
                strongNotificationList: [payload, ...state.strongNotificationList],
            };
        },
        clearStrongNotification(state: State) {
            return {
                ...state,
                strongNotificationList: [],
            };
        },
        closeStrongNotification(state: State, payload: { wmEPoiMsgId: string }) {
            return {
                ...state,
                strongNotificationList: state.strongNotificationList.filter(
                    item => item.wmEPoiMsgId !== payload.wmEPoiMsgId,
                ),
            };
        },
    },
    effects: () => {
        return {};
    },
};

export default pushNotification;
